import React, { useState, useCallback } from "react";
import { useFormikContext } from "formik";
import { KeyPersonnelForm } from "./KeyPersonnelForm";
import { ProjectExperienceTable } from "./ProjectExperienceTable";
import { ProjectDetailsModal } from "./ProjectDetailsModal";
import { ResumeTable } from "./ResumeTable";
import type {
	KeyPersonnelFormValues,
	KeyPersonnelProject,
	KeyPersonnelResume,
	ResumeFileInput,
} from "../types/KeyPersonnel.type";

interface KeyPersonnelFullFormProps {
	existingResumes?: KeyPersonnelResume[];
	profileTypeId: number;
}

const KeyPersonnelFullForm: React.FC<KeyPersonnelFullFormProps> = ({
	profileTypeId,
	existingResumes = [],
}) => {
	const { values, setFieldValue } = useFormikContext<KeyPersonnelFormValues>();
	const [projectModalOpen, setProjectModalOpen] = useState(false);
	const [editProjectIndex, setEditProjectIndex] = useState<number | null>(null);

	const handleProjectSave = useCallback(
		(project: KeyPersonnelProject) => {
			if (editProjectIndex !== null) {
				const updated = [...values.projects_completed];
				updated[editProjectIndex] = project;
				setFieldValue("projects_completed", updated);
			} else {
				setFieldValue("projects_completed", [
					...values.projects_completed,
					project,
				]);
			}
			setEditProjectIndex(null);
			setProjectModalOpen(false);
		},
		[editProjectIndex, values.projects_completed, setFieldValue],
	);

	const handleResumeAdd = useCallback(
		(file: File) => {
			const newEntry: ResumeFileInput = {
				id: null,
				file,
				is_default: values.resume_files.length === 0,
			};
			setFieldValue("resume_files", [...values.resume_files, newEntry]);
		},
		[values.resume_files, setFieldValue],
	);

	const handleResumeSetDefault = useCallback(
		(index: number) => {
			const updated = values.resume_files.map((rf, i) => ({
				...rf,
				is_default: i === index,
			}));
			setFieldValue("resume_files", updated);
		},
		[values.resume_files, setFieldValue],
	);

	const handleResumeSetPrivate = useCallback(
		() => {
			setFieldValue("is_resume_private", !values.is_resume_private);
		},
		[values.is_resume_private, setFieldValue],
	);

	const handleResumeRemove = useCallback(
		(index: number) => {
			const rf = values.resume_files[index];
			if (rf.id) {
				const updated = [...values.resume_files];
				updated[index] = { ...rf, is_remove: true };
				setFieldValue("resume_files", updated);
			} else {
				const updated = values.resume_files.filter((_, i) => i !== index);
				setFieldValue("resume_files", updated);
			}
		},
		[values.resume_files, setFieldValue],
	);

	return (
		<>
			<KeyPersonnelForm profileTypeId={profileTypeId}>
				{/* Project Experience */}
				<div>
					<label className="mb-2 block text-sm font-medium text-gray-700">
						Project Experience
					</label>
					<ProjectExperienceTable
						projects={values.projects_completed}
						onAdd={() => {
							setEditProjectIndex(null);
							setProjectModalOpen(true);
						}}
						onEdit={(i) => {
							setEditProjectIndex(i);
							setProjectModalOpen(true);
						}}
						onRemove={(i) => {
							const updated = values.projects_completed.filter(
								(_, idx) => idx !== i
							);
							setFieldValue("projects_completed", updated);
						}}
					/>
				</div>

				{/* Resumes */}
				<div>
					<label className="mb-2 block text-sm font-medium text-gray-700">
						Resumes
					</label>
					<ResumeTable
						isResumePrivate={values.is_resume_private}
						resumes={existingResumes}
						resumeFiles={values.resume_files}
						onAdd={handleResumeAdd}
						onSetDefault={handleResumeSetDefault}
						onChangePrivate={handleResumeSetPrivate}
						onRemove={handleResumeRemove}
					/>
				</div>
			</KeyPersonnelForm>

			{/* Project Details sub-modal */}
			<ProjectDetailsModal
				isOpen={projectModalOpen}
				onClose={() => {
					setEditProjectIndex(null);
					setProjectModalOpen(false);
				}}
				onSave={handleProjectSave}
				editProject={
					editProjectIndex !== null
						? values.projects_completed[editProjectIndex]
						: undefined
				}
				excludeProjectIds={
					values.projects_completed
						.filter((p, i) => p.project_id != null && i !== editProjectIndex)
						.map((p) => p.project_id as number)
				}
				currentPosition={values.position}
			/>
		</>
	);
};

export { KeyPersonnelFullForm };
export type { KeyPersonnelFullFormProps };
